import {type ConfiguredQuickEntryItem, type QuickEntryItem} from "../models";
import {type ThunkAction} from "redux-thunk";
import {type RootState} from "../../redux/store";
import {type AnyAction} from "../../redux/types";
import {type LineItemBuilder} from "../../utils/cartUpdating/LineItemUpdate";
import {type LineItem, PackageEntrySource, type PlainLineItem} from "../../Cart/models/CartData";
import {getNextUID} from "../../utils/uid";
import * as WeightUtils from "../../utils/WeightUtils";
import {INVALID_PACKAGE_CODE} from "../../utils/constants";
import {newChangeObject} from "../../utils/cartUpdating/common";
import {groupByFn, isNonNull, toMapFn} from "../../utils/reducers";
import {refreshDeliveriesForProduct} from "../../utils/cartUpdating/delivery/refreshEdd";
import {
  MSG_COULD_NOT_PRESELECT_PACKAGE,
  MSG_PACKAGE_CHANGED,
  refreshAvailabilityLoadNonContract,
} from "../../utils/cartUpdating/refreshPackage";
import {refreshPriceInfoData} from "../../utils/cartUpdating/refreshPriceInfoData";
import {refreshDeliveryDate} from "../../utils/cartUpdating/delivery/refreshDate";
import {validatePackage, validateQty} from "../../utils/cartUpdating/validations/validateGeneralLineItem";
import {validateEntireProductQty} from "../../utils/cartUpdating/validations/validateGeneralProduct";
import {sendToHybris, sendToRedux} from "../../Cart/redux/actions/cartQuoteCommonActions";
import {INVALID_ENTRY_ID} from "../constants";
import {hasProductConfigured} from "../helpers";
import {
  isPackageRestrictionEnabled
} from "../../common/components/PackageRestrictionContext/utils/isPackageRestrictionEnabled";
import {ProductService} from "../../AddToCart/ProductService";
import {getProductData, isKITProduct, type ProductData} from "../../models/ProductData";
import {selectLineItemsByProduct} from "~/Cart/redux/selectors/cartSelectors";
import {cartPageLoadThunk} from "~/Cart/pages/CartPage/redux/cartPageLoadThunk";
import {userIsCustomer} from "~/utils/roles";
import {requestBaseProductData} from "~/redux/products/actions";
import type {ProductDescriptor} from "~/feature/ProductDescriptor/v1/types";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

export const addProductsToCartAction = (items: QuickEntryItem[]): ThunkResult<Promise<void>> => {
  return (dispatch, getState) => {
    if (userIsCustomer()) {
      return dispatch(addProductsToCartActionB2B(items));
    } else {
      return dispatch(addProductsToCartActionB2C(items));
    }
  };
};

export const addProductsToCartActionB2B = (items: QuickEntryItem[]): ThunkResult<Promise<void>> => {
  return async (dispatch, getState) => {
    // Sanity check - items without products should not be passed here
    const configuredEntries = items.filter(hasProductConfigured);

    if (configuredEntries.length === 0) {
      return;
    }

    const state = getState();

    const getPriceRef = (item: ConfiguredQuickEntryItem) => (item.priceReference === "-" ? "" : item.priceReference);

    const promises = configuredEntries
      .flatMap((item) => {
        const { promise, current } = ProductService.getProductDescriptor(item.product.code, getPriceRef(item), {
          fetch: true,
        });
        return [
          promise,
          ...(isKITProduct(current.base)
            ? current.base.optionalParts.map(
                (subProduct) =>
                  ProductService.getProductDescriptor(subProduct.product.code, getPriceRef(item), { fetch: true })
                    .promise,
              )
            : []),
        ];
      })
      .filter(isNonNull);

    const productDescriptors = await Promise.all(promises);

    const descriptorsByProductCode = groupByFn(productDescriptors, (item) => item.base.code);

    const descriptorsByCodeByPriceRef: Dict<Dict<ProductDescriptor>> = {};
    Object.entries(descriptorsByProductCode).forEach(([productCode, descriptors]) => {
      descriptorsByCodeByPriceRef[productCode] = toMapFn(descriptors, (item) => item.priceReference);
    });

    const transform = (item: ConfiguredQuickEntryItem) =>
      transformItem(item, descriptorsByCodeByPriceRef[item.product?.code || ""][getPriceRef(item)]);

    const transformedEntries = configuredEntries.map(transform);
    let allNewLineItems = transformedEntries.map((_) => _.lineItem);
    const allNewOptionalParts: LineItemBuilder[] = transformedEntries.flatMap((_) => _.subLineItems);

    const containsOptionals = allNewOptionalParts.length > 0;
    allNewLineItems = allNewLineItems.concat(allNewOptionalParts);

    const newLineItemsByCode = groupByFn(
      allNewLineItems,
      (item) => item.getProductCode(),
      (item) => item,
    );

    const existingLineItemsByCode = groupByFn(
      Object.values(state.cart.packEntriesById).map(newChangeObject),
      (item) => item.getProductCode(),
      (item) => item,
    );

    const lineItemsByProduct = selectLineItemsByProduct(state);
    const cartEntryNumberByProductCode = toMapFn(
      Object.values(lineItemsByProduct),
      (item) => item[0].productCode,
      (item) => item.filter((i) => i.cartEntryNumber !== INVALID_ENTRY_ID)[0].cartEntryNumber,
    );

    const cartEntryIdByProductCode = toMapFn(
      Object.values(lineItemsByProduct),
      (item) => item[0].productCode,
      (item) => item[0].cartEntryId,
    );

    allNewLineItems.forEach((item) => {
      const cartEntryId = cartEntryIdByProductCode[item.getProductCode()] ?? INVALID_ENTRY_ID;
      const cartEntryNumber = cartEntryNumberByProductCode[item.getProductCode()] ?? INVALID_ENTRY_ID;
      if (cartEntryId !== INVALID_ENTRY_ID) {
        item.set("cartEntryId", cartEntryId);
        item.set("cartEntryNumber", cartEntryNumber);
      }
    });

    // These need to go through a full refresh
    const merged = Object.keys(newLineItemsByCode).reduce(
      (map, productCode) => {
        map[productCode] = [...(existingLineItemsByCode[productCode] || []), ...newLineItemsByCode[productCode]];
        return map;
      },
      {} as Dict<LineItemBuilder[]>,
    );

    Object.keys(merged).forEach((productCode) => {
      orchestrateRefreshProductForATC(merged[productCode], descriptorsByCodeByPriceRef[productCode]);
    });

    const allLineItems = Object.values(merged).flat();

    // We are expected to preselect the items, so no message
    allNewLineItems.forEach((item) => {
      item.removeMessage(MSG_COULD_NOT_PRESELECT_PACKAGE.key);
      item.removeMessage(MSG_PACKAGE_CHANGED.key);
    });

    if (containsOptionals) {
      dispatch(sendToRedux(allLineItems));
      await Promise.all(
        Object.values(merged).map((lineItems) => {
          return dispatch(sendToHybris(lineItems.filter((item) => !allNewOptionalParts.includes(item))));
        }),
      );
      dispatch(cartPageLoadThunk());
    } else {
      dispatch(sendToRedux(allLineItems));
      await Promise.all(Object.values(merged).map((lineItems) => dispatch(sendToHybris(lineItems))));
    }
  };
};

export const addProductsToCartActionB2C = (items: QuickEntryItem[]): ThunkResult<Promise<void>> => {
  return async (dispatch, getState) => {
    // Sanity check - items without products should not be passed here
    const configuredEntries = items.filter(hasProductConfigured);

    if (configuredEntries.length === 0) {
      return;
    }

    const state = getState();

    const products = await Promise.all(
      configuredEntries.map((item) => dispatch(requestBaseProductData(item.product.code))),
    );
    const productByCode = toMapFn(products, (item) => item.code);

    const transform = (item: ConfiguredQuickEntryItem) => transformItem(item, productByCode[item.product.code]);

    const transformedEntries = configuredEntries.map(transform);
    let allNewLineItems = transformedEntries.map((_) => _.lineItem);
    const allNewOptionalParts: LineItemBuilder[] = transformedEntries.flatMap((_) => _.subLineItems);

    const containsOptionals = allNewOptionalParts.length > 0;
    allNewLineItems = allNewLineItems.concat(allNewOptionalParts);

    const newLineItemsByCode = groupByFn(
      allNewLineItems,
      (item) => item.getProductCode(),
      (item) => item,
    );

    const existingLineItemsByCode = groupByFn(
      Object.values(state.cart.packEntriesById).map(newChangeObject),
      (item) => item.getProductCode(),
      (item) => item,
    );

    const lineItemsByProduct = selectLineItemsByProduct(state);
    const cartEntryNumberByProductCode = toMapFn(
      Object.values(lineItemsByProduct),
      (item) => item[0].productCode,
      (item) => item.filter((i) => i.cartEntryNumber !== INVALID_ENTRY_ID)[0].cartEntryNumber,
    );

    const cartEntryIdByProductCode = toMapFn(
      Object.values(lineItemsByProduct),
      (item) => item[0].productCode,
      (item) => item[0].cartEntryId,
    );

    allNewLineItems.forEach((item) => {
      const cartEntryId = cartEntryIdByProductCode[item.getProductCode()] ?? INVALID_ENTRY_ID;
      const cartEntryNumber = cartEntryNumberByProductCode[item.getProductCode()] ?? INVALID_ENTRY_ID;
      if (cartEntryId !== INVALID_ENTRY_ID) {
        item.set("cartEntryId", cartEntryId);
        item.set("cartEntryNumber", cartEntryNumber);
      }
    });

    // These need to go through a full refresh
    const merged = Object.keys(newLineItemsByCode).reduce(
      (map, productCode) => {
        map[productCode] = [...(existingLineItemsByCode[productCode] || []), ...newLineItemsByCode[productCode]];
        return map;
      },
      {} as Dict<LineItemBuilder[]>,
    );

    const allLineItems = Object.values(merged).flat();

    // We are expected to preselect the items, so no message
    allNewLineItems.forEach((item) => {
      item.removeMessage(MSG_COULD_NOT_PRESELECT_PACKAGE.key);
      item.removeMessage(MSG_PACKAGE_CHANGED.key);
    });

    // Force item.hasUpdate to be true
    allLineItems.forEach((item) => {
      item.set("productCode", item.getProductCode());
    });

    if (containsOptionals) {
      dispatch(sendToRedux(allLineItems));
      await Promise.all(
        Object.values(merged).map((lineItems) => {
          return dispatch(sendToHybris(lineItems.filter((item) => !allNewOptionalParts.includes(item))));
        }),
      );
      dispatch(cartPageLoadThunk());
    } else {
      dispatch(sendToRedux(allLineItems));
      await Promise.all(Object.values(merged).map((lineItems) => dispatch(sendToHybris(lineItems))));
    }
  };
};

function orchestrateRefreshProductForATC(
  allLineItems: LineItemBuilder[],
  descriptorsByPriceReference: Dict<ProductDescriptor>,
) {
  const anyDescriptor = Object.values(descriptorsByPriceReference)[0];
  const availability = anyDescriptor.availability;

  const lineItemsByType = groupByFn(
    allLineItems,
    (item) => item.get("source"),
    (item) => item,
  );
  const lineItemsWithUpdateablePrice = [
    ...(lineItemsByType[PackageEntrySource.DEFAULT] || []),
    ...(lineItemsByType[PackageEntrySource.BULK] || []),
  ];
  const lineItemsWithUpdateableEDD = [
    ...(lineItemsByType[PackageEntrySource.DEFAULT] || []),
    ...(lineItemsByType[PackageEntrySource.BULK] || []),
    ...(lineItemsByType[PackageEntrySource.QUOTE] || []),
  ];

  // 2.1. First availability => We may need to preselect packages.
  lineItemsWithUpdateablePrice.forEach((lineItem) =>
    refreshAvailabilityLoadNonContract(lineItem, availability!, anyDescriptor),
  );

  // 2.2. Then prices.
  refreshPriceInfoData(allLineItems, descriptorsByPriceReference);

  // 3. Refresh the Estimated Delivery Date.
  refreshDeliveriesForProduct(lineItemsWithUpdateableEDD, availability!);

  // 4. Refresh requested delivery date
  allLineItems.forEach(refreshDeliveryDate);

  // 5. Various qty and package validations
  allLineItems.forEach(validateQty);
  validateEntireProductQty(allLineItems, Object.values(descriptorsByPriceReference)[0]);
  allLineItems.forEach(validatePackage);
}

function transformItem(
  data: QuickEntryItem,
  productDescriptor: ProductDescriptor | ProductData,
): { lineItem: LineItemBuilder; subLineItems: LineItemBuilder[] } {
  const product = getProductData(productDescriptor);
  const item: PlainLineItem = {
    ...makeEmptyLineItem(product.code),
    comment: data.comment,
    deliveryDate: data.requestedDeliveryDate,
    packCode: data.packCode ? data.packCode : INVALID_PACKAGE_CODE,
    quantity: data.quantity,
    weight: product.weight ?? WeightUtils.zero(),
    priceReference: data.priceReference,

    restrictedPackage: isPackageRestrictionEnabled() ? !!data.packCode : false,
    optionalPartIds: [],
  };

  let subLineItems: LineItem[] = [];
  if (isKITProduct(product)) {
    subLineItems = product.optionalParts.map((part) => {
      return {
        ...makeEmptyLineItem(part.product.code),
        parentLineItemId: item.id,
        deliveryDate: data.requestedDeliveryDate,
        quantity: part.quantity * data.quantity,
        entryNumber: -1,
        weight: part.product.weight ?? WeightUtils.zero(),
        priceReference: data.priceReference,
      };
    });
    item.optionalPartIds = subLineItems.map((item) => item.id);
  }

  return {
    lineItem: newChangeObject(item),
    subLineItems: subLineItems.map(newChangeObject),
  };
}

const makeEmptyLineItem = (productCode: string): LineItem => {
  return {
    pk: INVALID_ENTRY_ID,
    cartEntryPk: INVALID_ENTRY_ID,
    id: getNextUID(),
    productCode: productCode,
    cartEntryId: INVALID_ENTRY_ID, // TODO ?
    cartEntryNumber: INVALID_ENTRY_ID,
    comment: "",
    deliveryDate: new Date(),
    manuallySetRDD: true,
    packSize: 1,
    packCode: INVALID_PACKAGE_CODE,
    quantity: 1,
    weight: WeightUtils.zero(),
    netPrice: undefined,
    valid: true,
    entryNumber: null,
    primaryWarehouseName: "",
    primaryWarehouseCode: "",
    messages: [],
    disableEdit: false,
    source: PackageEntrySource.DEFAULT,
    showComments: false,
    priceReference: "",

    edd: null,
    isInStock: null,

    // Salesman fields
    internalComment: "",
    priceOverriden: false,

    // quote fields
    quoteNumber: "",
    quoteLine: "",
    minimumQty: 1,
    contractNumber: "",
    contractLine: "",
    estimatedShippingDate: null,
    quoteExpiryDate: null,
    original: true,

    // UI Fields
    isPackagingSelectionOpen: false,

    restrictedPackage: false,

    optionalPartIds: [],

    customerOrderNumber: "",
    countryOfOrigin: "",
  };
};
